import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { historiasClinicasAPI, clientesAPI } from '../services/api';
import ModalCobrarAtencion from '../components/ModalCobrarAtencion';

// ─── VALORES INICIALES ───
const initialForm = {
  cliente_id: '',
  numero_factura: '',
  fecha_registro: '',
  motivo_consulta: '',
  antecedentes_familiares: '',
  antecedentes_personales: '',
  // AV Distancia
  av_distancia_od_sc: '', av_distancia_oi_sc: '', av_distancia_ao_sc: '',
  av_distancia_od_cc: '', av_distancia_oi_cc: '', av_distancia_ao_cc: '',
  av_distancia_od_ph: '', av_distancia_oi_ph: '',
  // AV Proxima
  av_proxima_od_sc: '', av_proxima_oi_sc: '', av_proxima_ao_sc: '',
  av_proxima_od_cc: '', av_proxima_oi_cc: '', av_proxima_ao_cc: '',
  av_proxima_od_ph: '', av_proxima_oi_ph: '', av_proxima_ao_ph: '',
  ojo_dominante_od: '', ojo_dominante_oi: '',
  // JSONB
  lensometria_od: { esf: '', cyl: '', eje: '', add: '', dp: '', alt: '' },
  lensometria_oi: { esf: '', cyl: '', eje: '', add: '', dp: '', alt: '' },
  queratometria_od: { k1: '', k2: '', eje: '' },
  queratometria_oi: { k1: '', k2: '', eje: '' },
  autorefractor_od: { esf: '', cyl: '', eje: '', av: '' },
  autorefractor_oi: { esf: '', cyl: '', eje: '', av: '' },
  subjetivo_od: { esf: '', cyl: '', eje: '', av: '' },
  subjetivo_oi: { esf: '', cyl: '', eje: '', av: '' },
  rx_final_od: { esf: '', cyl: '', eje: '', avl: '', avc: '', ao: '', dp: '', np: '', add: '', alt: '' },
  rx_final_oi: { esf: '', cyl: '', eje: '', avl: '', avc: '', ao: '', dp: '', np: '', add: '', alt: '' },
  contacto_od: { poder: '', curva_base: '', diametro: '', adicion: '', diseno: '', material: '' },
  contacto_oi: { poder: '', curva_base: '', diametro: '', adicion: '', diseno: '', material: '' },
  hirschberg_od: '', hirschberg_oi: '',
  campimetria_od: { t100: '', n60: '', s60: '', i70: '' },
  campimetria_oi: { t100: '', n60: '', s60: '', i70: '' },
  cover_test_od: '', cover_test_oi: '',
  foria: '', tropia: '', magnitud_desviacion: '',
  mov_oculares_od: '', mov_oculares_oi: '',
  ppc_od: '', ppc_oi: '', or_od: '', or_oi: '',
  luz_fr: '', tecnica: '', ciclos: '', kappa: '',
  distancia: '', arp: '', arn: '', rfp: '', rfn: '',
  // Luces Worth, Ishihara, PIO
  luces_worth_lejos: '', luces_worth_cerca: '',
  test_ishihara_od: '', test_ishihara_oi: '',
  presion_intraocular_od: '', presion_intraocular_oi: '',
  test_adicionales: '',
  // Biomicroscopia
  biomicroscopia_od: {
    cornea: '', cristalino: '', pupila: '', iris: '', pestanas: '',
    conjuntiva_bulbar: '', conjuntiva_tarsal: '', orbita: '',
    pliegue_semilunar: '', caruncula: '', conductos_lagrimales: '',
    parpado_superior: '', camara_anterior: '', parpado_inferior: ''
  },
  biomicroscopia_oi: {
    cornea: '', cristalino: '', pupila: '', iris: '', pestanas: '',
    conjuntiva_bulbar: '', conjuntiva_tarsal: '', orbita: '',
    pliegue_semilunar: '', caruncula: '', conductos_lagrimales: '',
    parpado_superior: '', camara_anterior: '', parpado_inferior: ''
  },
  biomicroscopia_otros: '',
  reflejo_acomodativo: { ao_miosis: '', ao_convergencia: '' },
  reflejo_fotomotor: { miosis: '', midriasis: '' },
  reflejo_consensual_od: '', reflejo_consensual_oi: '',
  fondo_ojo_od: {
    av_temp_sup: '', av_temp_inf: '', av_nasal_sup: '', av_nasal_inf: '',
    retina: '', macula: '', excavacion: '', vasos: '', papila: '',
    fijacion: '', color: '', borde: ''
  },
  fondo_ojo_oi: {
    av_temp_sup: '', av_temp_inf: '', av_nasal_sup: '', av_nasal_inf: '',
    retina: '', macula: '', excavacion: '', vasos: '', papila: '',
    fijacion: '', color: '', borde: ''
  },
  diagnostico: '', tratamiento: '', observacion: '', disposicion: '', conducta: '',
  presion_sistolica: '', presion_diastolica: '', saturacion_o2: '',
  glucosa: '', trigliceridos: '', ttp: '', atp: '', colesterol: '',
  estado: 'completada', notas_adicionales: '',
  generar_prescripcion: true
};

const AV_OPTIONS = [
  '', '20/20', '20/25', '20/30', '20/40', '20/50', '20/60', '20/70',
  '20/80', '20/100', '20/120', '20/150', '20/200', '20/400',
  'CD 3m', 'CD 2m', 'CD 1m', 'MM', 'PL', 'NPL'
];

const STEPS = [
  { key: 'paciente', label: 'Paciente', icon: 'bi-person-fill' },
  { key: 'motivo', label: 'Motivo', icon: 'bi-chat-left-text-fill' },
  { key: 'agudeza', label: 'Agudeza Visual', icon: 'bi-eye-fill' },
  { key: 'refraccion', label: 'Refracción', icon: 'bi-eyeglasses' },
  { key: 'rx_final', label: 'RX Final', icon: 'bi-prescription2' },
  { key: 'motilidad', label: 'Motilidad', icon: 'bi-arrows-move' },
  { key: 'binocular', label: 'Binocular', icon: 'bi-binoculars-fill' },
  { key: 'biomicro', label: 'Biomicroscopía', icon: 'bi-microscope' },
  { key: 'fondo', label: 'Fondo de Ojo', icon: 'bi-circle' },
  { key: 'diagnostico', label: 'Diagnóstico', icon: 'bi-clipboard2-pulse-fill' }
];

// ─── MINI INPUT PARA TABLAS ───
const TInput = ({ value, onChange, placeholder, style, className = '' }) => (
  <input type="text" className={`form-control form-control-sm text-center ${className}`}
    value={value || ''} onChange={onChange} placeholder={placeholder}
    style={{ fontSize: '0.8rem', padding: '2px 4px', ...style }}
    onKeyDown={e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const inputs = Array.from(e.target.closest('table')?.querySelectorAll('input[type="text"]') || []);
        const idx = inputs.indexOf(e.target);
        if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus();
      }
    }} />
);

const AvSel = ({ value, onChange }) => (
  <select className="form-select form-select-sm text-center"
    value={value || ''} onChange={onChange}
    style={{ fontSize: '0.75rem', padding: '2px 2px', minWidth: 70 }}>
    {AV_OPTIONS.map(o => <option key={o} value={o}>{o || '-'}</option>)}
  </select>
);

// ─── SVG OJO FRONTAL (Biomicroscopía) ───
const EyeSvg = ({ side }) => (
  <svg viewBox="0 0 150 120" style={{ width: '100%', maxWidth: 180 }}>
    {/* Ceja */}
    <path d={side === 'OD'
      ? "M20,25 Q75,0 130,25"
      : "M20,25 Q75,0 130,25"}
      fill="none" stroke="#333" strokeWidth="3" />
    {/* Párpado superior */}
    <path d="M25,45 Q75,15 125,45" fill="none" stroke="#333" strokeWidth="2" />
    {/* Párpado inferior */}
    <path d="M30,80 Q75,100 120,80" fill="none" stroke="#333" strokeWidth="2" />
    {/* Forma del ojo */}
    <ellipse cx="75" cy="62" rx="47" ry="22" fill="white" stroke="#333" strokeWidth="1.5" />
    {/* Iris */}
    <circle cx="75" cy="62" r="16" fill="#8B6914" stroke="#333" strokeWidth="1" />
    {/* Pupila */}
    <circle cx="75" cy="62" r="7" fill="#111" />
    {/* Reflejo */}
    <circle cx="71" cy="58" r="2.5" fill="white" opacity="0.8" />
    {/* Pestañas superiores */}
    {[30,40,50,60,70,80,90,100,110,120].map((x,i) => {
      const y1 = 45 - Math.abs(75-x)*0.4;
      return <line key={i} x1={x} y1={y1} x2={x + (x<75?-3:3)} y2={y1-8} stroke="#333" strokeWidth="1.2" />;
    })}
    {/* Label */}
    <text x="75" y="115" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#0d6efd">{side}</text>
  </svg>
);

// ─── SVG FONDO DE OJO ───
const FundusSvg = ({ side }) => (
  <svg viewBox="0 0 160 160" style={{ width: '100%', maxWidth: 170 }}>
    {/* Fondo circular */}
    <circle cx="80" cy="80" r="75" fill="#B85C38" stroke="#333" strokeWidth="2" />
    {/* Disco óptico */}
    <ellipse cx={side === 'OD' ? 55 : 105} cy="80" rx="18" ry="20" fill="#FFDAB9" stroke="#E8A87C" strokeWidth="1" />
    <ellipse cx={side === 'OD' ? 55 : 105} cy="80" rx="8" ry="10" fill="#FFE4C4" opacity="0.6" />
    {/* Mácula */}
    <circle cx={side === 'OD' ? 105 : 55} cy="80" r="10" fill="#8B4513" opacity="0.5" />
    <circle cx={side === 'OD' ? 105 : 55} cy="80" r="4" fill="#654321" opacity="0.3" />
    {/* Vasos principales */}
    {/* Arterias/venas superiores */}
    <path d={side === 'OD'
      ? "M55,65 Q70,40 110,20"
      : "M105,65 Q90,40 50,20"}
      fill="none" stroke="#C41E3A" strokeWidth="2" opacity="0.7" />
    <path d={side === 'OD'
      ? "M55,63 Q65,45 100,25"
      : "M105,63 Q95,45 60,25"}
      fill="none" stroke="#8B0000" strokeWidth="1.5" opacity="0.6" />
    {/* Vasos inferiores */}
    <path d={side === 'OD'
      ? "M55,95 Q70,120 110,140"
      : "M105,95 Q90,120 50,140"}
      fill="none" stroke="#C41E3A" strokeWidth="2" opacity="0.7" />
    <path d={side === 'OD'
      ? "M55,97 Q65,115 100,135"
      : "M105,97 Q95,115 60,135"}
      fill="none" stroke="#8B0000" strokeWidth="1.5" opacity="0.6" />
    <text x="80" y="170" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#0d6efd">{side}</text>
  </svg>
);

// ─── COMPONENTE PRINCIPAL ───
const HistoriaClinica = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...initialForm });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clienteBuscar, setClienteBuscar] = useState('');
  const [clienteResultados, setClienteResultados] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showCobrarModal, setShowCobrarModal] = useState(false);
  const [atencionCobrada, setAtencionCobrada] = useState(null);

  // Modal Crear Paciente Rapido (desde HC, sin salir de la seccion)
  const [showCrearPaciente, setShowCrearPaciente] = useState(false);
  const [nuevoPac, setNuevoPac] = useState({
    tipo_identificacion: 'cedula',
    identificacion: '',
    nombres: '',
    apellidos: '',
    fecha_nacimiento: '',
    genero: '',
    celular: '',
    email: '',
    ocupacion: ''
  });
  const [guardandoPac, setGuardandoPac] = useState(false);

  // Pacientes recientes (6 ultimos) para seleccion rapida sin buscar
  const [pacientesRecientes, setPacientesRecientes] = useState([]);

  const isEditing = !!id;

  // Cargar HC existente
  useEffect(() => {
    if (id) {
      setLoading(true);
      historiasClinicasAPI.getById(id).then(res => {
        if (res.data.success) {
          const hc = res.data.data;
          const merged = { ...initialForm };
          for (const key of Object.keys(merged)) {
            if (hc[key] !== undefined && hc[key] !== null) {
              if (typeof merged[key] === 'object' && !Array.isArray(merged[key]) && typeof hc[key] === 'object') {
                merged[key] = { ...merged[key], ...hc[key] };
              } else {
                merged[key] = hc[key];
              }
            }
          }
          // Mapear fecha_consulta (o created_at) a fecha_registro para poder editar la fecha
          const fechaOrigen = hc.fecha_consulta || hc.created_at;
          if (fechaOrigen) {
            const d = new Date(fechaOrigen);
            merged.fecha_registro = d.toISOString().slice(0, 16); // formato datetime-local
          }
          setForm(merged);
          setClienteSeleccionado({
            id: hc.cliente_id,
            nombres: hc.cliente_nombre?.split(' ')[0] || '',
            apellidos: hc.cliente_nombre?.split(' ').slice(1).join(' ') || '',
            identificacion: hc.cliente_ci,
            fecha_nacimiento: hc.cliente_nacimiento,
            celular: hc.cliente_celular,
            ocupacion: hc.cliente_ocupacion
          });
        }
      }).catch(() => toast.error('Error al cargar HC')).finally(() => setLoading(false));
    } else {
      const clienteId = searchParams.get('cliente');
      if (clienteId) {
        clientesAPI.getById(clienteId).then(res => {
          if (res.data.success) {
            const c = res.data.data;
            setClienteSeleccionado(c);
            setForm(f => ({ ...f, cliente_id: c.id }));
            setStep(1);
          }
        }).catch(() => {});
      }
    }
  }, [id, searchParams]);

  // Cargar 6 pacientes recientes al montar (solo si no viene cliente preseleccionado ni editando)
  useEffect(() => {
    if (id) return;
    clientesAPI.getAll({ limit: 6, page: 1 }).then(res => {
      if (res.data.success) setPacientesRecientes(res.data.data || []);
    }).catch(() => {});
  }, [id]);

  const crearPacienteRapido = async () => {
    const id = (nuevoPac.identificacion || '').trim();
    // Validacion formato Ecuador por tipo_identificacion
    if (nuevoPac.tipo_identificacion === 'cedula') {
      if (!/^\d{10}$/.test(id)) { toast.error('Cédula ecuatoriana debe tener 10 dígitos numéricos'); return; }
    } else if (nuevoPac.tipo_identificacion === 'ruc') {
      if (!/^\d{13}$/.test(id)) { toast.error('RUC ecuatoriano debe tener 13 dígitos numéricos'); return; }
    } else if (nuevoPac.tipo_identificacion === 'pasaporte') {
      if (id.length < 5 || id.length > 20) { toast.error('Pasaporte debe tener entre 5 y 20 caracteres'); return; }
    }
    const nombresT = (nuevoPac.nombres || '').trim();
    const apellidosT = (nuevoPac.apellidos || '').trim();
    if (nombresT.length < 2) { toast.error('Nombres requeridos (mín 2 caracteres)'); return; }
    if (apellidosT.length < 2) { toast.error('Apellidos requeridos (mín 2 caracteres)'); return; }
    // Email formato valido si se provee
    if (nuevoPac.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoPac.email.trim())) {
      toast.error('Email con formato inválido'); return;
    }
    try {
      setGuardandoPac(true);
      const res = await clientesAPI.create({ ...nuevoPac, identificacion: id, nombres: nombresT, apellidos: apellidosT });
      if (res.data.success) {
        const c = res.data.data;
        toast.success('Paciente creado');
        seleccionarCliente(c);
        setShowCrearPaciente(false);
        setNuevoPac({ tipo_identificacion: 'cedula', identificacion: '', nombres: '', apellidos: '', fecha_nacimiento: '', genero: '', celular: '', email: '', ocupacion: '' });
        // refrescar lista de recientes
        clientesAPI.getAll({ limit: 6, page: 1 }).then(r => r.data.success && setPacientesRecientes(r.data.data || [])).catch(() => {});
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Error al crear paciente');
    } finally {
      setGuardandoPac(false);
    }
  };

  // Buscar cliente
  const buscarCliente = useCallback(async (texto) => {
    if (!texto || texto.length < 2) { setClienteResultados([]); return; }
    setBuscandoCliente(true);
    try {
      const res = await clientesAPI.getAll({ buscar: texto, limit: 8 });
      if (res.data.success) setClienteResultados(res.data.data || []);
    } catch { /* */ }
    setBuscandoCliente(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscarCliente(clienteBuscar), 400);
    return () => clearTimeout(t);
  }, [clienteBuscar, buscarCliente]);

  // FIX CRITICO anti-mezcla: si el usuario cambia de paciente mientras edita una HC,
  // no permitir que el form herede datos del paciente anterior. Esto cubre el caso
  // "empezaste con A, cambiaste a B" que causo el bug Rocio/Samantha.
  // Cuando cambia clienteSeleccionado.id, sincroniza form.cliente_id (o lo limpia).
  const clienteSeleccionadoId = clienteSeleccionado?.id;
  useEffect(() => {
    if (!clienteSeleccionadoId) {
      setForm(f => (f.cliente_id ? { ...f, cliente_id: '' } : f));
      return;
    }
    setForm(f => Number(f.cliente_id) !== Number(clienteSeleccionadoId)
      ? { ...f, cliente_id: clienteSeleccionadoId }
      : f);
  }, [clienteSeleccionadoId]);

  const seleccionarCliente = (c) => {
    setClienteSeleccionado(c);
    setForm(f => ({ ...f, cliente_id: c.id }));
    setClienteResultados([]);
    setClienteBuscar('');
  };

  const calcEdad = (fn) => {
    if (!fn) return '-';
    const h = new Date(), n = new Date(fn);
    let e = h.getFullYear() - n.getFullYear();
    if (h.getMonth() < n.getMonth() || (h.getMonth() === n.getMonth() && h.getDate() < n.getDate())) e--;
    return `${e} años`;
  };

  // Handlers de form
  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setJsonField = (key, subKey, val) => setForm(f => ({ ...f, [key]: { ...f[key], [subKey]: val } }));

  const toggleSection = (key) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  // Guardar
  const handleSubmit = async () => {
    // Guard anti doble-submit
    if (saving) return;

    if (!form.cliente_id) { toast.error('Seleccione un paciente'); setStep(0); return; }
    if (!clienteSeleccionado) { toast.error('No hay paciente activo en pantalla'); setStep(0); return; }

    // Validar consistencia cliente_id vs clienteSeleccionado (defensa en profundidad, el backend tambien valida)
    if (Number(form.cliente_id) !== Number(clienteSeleccionado.id)) {
      toast.error(`Inconsistencia: el paciente en pantalla (${clienteSeleccionado.nombres || ''} ${clienteSeleccionado.apellidos || ''}) no coincide con el del registro. Vuelve a seleccionar el paciente.`);
      setStep(0);
      setForm(f => ({ ...f, cliente_id: '' }));
      setClienteSeleccionado(null);
      return;
    }

    // Confirmacion explicita
    const pacienteTxt = `${clienteSeleccionado.nombres || ''} ${clienteSeleccionado.apellidos || ''}`.trim() + (clienteSeleccionado.identificacion ? ` (${clienteSeleccionado.identificacion})` : '');
    if (!window.confirm(`${isEditing ? 'Actualizar' : 'Guardar'} historia clínica para:\n\n${pacienteTxt}\n\n¿Confirmar?`)) {
      return;
    }

    // FIX CRITICO: enviar cliente_identificacion_confirmacion al backend. El backend
    // comparara la identificacion con la del cliente_id y rechazara si no coinciden.
    // Esto blinda contra: state stale en React, tabs duplicadas, uso directo del API,
    // navegacion con boton atras del navegador, cualquier otro flujo que podria
    // causar mezcla.
    const payload = {
      ...form,
      cliente_identificacion_confirmacion: clienteSeleccionado.identificacion || null
    };

    setSaving(true);
    try {
      if (isEditing) {
        await historiasClinicasAPI.update(id, payload);
        toast.success('Historia clínica actualizada');
      } else {
        await historiasClinicasAPI.create(payload);
        toast.success('Historia clínica creada');
      }
      navigate('/historias-clinicas');
    } catch (err) {
      const code = err.response?.data?.error;
      const msg = err.response?.data?.message || err.response?.data?.error || 'Error al guardar';
      if (code === 'INCONSISTENCIA_CLIENTE') {
        toast.error(`Inconsistencia detectada por servidor: ${msg}. Recarga la página y selecciona nuevamente.`);
      } else {
        toast.error(msg);
      }
    }
    setSaving(false);
  };

  // Navegación
  const nextStep = () => {
    if (step === 0 && !clienteSeleccionado) { toast.warning('Seleccione un paciente'); return; }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const prevStep = () => { if (step > 0) setStep(step - 1); };

  // ─── SECTION HEADER ───
  const SectionTitle = ({ icon, title, color = 'primary' }) => (
    <div className={`d-flex align-items-center gap-2 mb-3 pb-2 border-bottom border-${color}`}>
      <i className={`bi ${icon} text-${color} fs-5`}></i>
      <h6 className={`mb-0 text-${color} fw-bold text-uppercase`} style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>{title}</h6>
    </div>
  );

  // ─── RENDER STEPS ───
  const renderStep = () => {
    switch (STEPS[step].key) {
      // ══════════════════════════════════════
      // PASO 1: PACIENTE
      // ══════════════════════════════════════
      case 'paciente':
        return (
          <div>
            <SectionTitle icon="bi-person-fill" title="Datos del Paciente" />
            {!clienteSeleccionado ? (
              <div className="position-relative">
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <div className="input-group flex-grow-1" style={{ minWidth: 260 }}>
                    <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
                    <input type="text" className="form-control" placeholder="Buscar por CI, nombre o celular..."
                      value={clienteBuscar} onChange={e => setClienteBuscar(e.target.value)} autoFocus />
                    {buscandoCliente && <span className="input-group-text bg-white"><div className="spinner-border spinner-border-sm text-muted"></div></span>}
                  </div>
                  <button type="button" className="btn" style={{ background: '#1B4F72', color: 'white' }} onClick={() => setShowCrearPaciente(true)}>
                    <i className="bi bi-plus-lg me-1"></i>Nuevo paciente
                  </button>
                </div>
                {clienteResultados.length > 0 && (
                  <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, maxWidth: 'calc(100% - 0px)' }}>
                    {clienteResultados.map(c => (
                      <button key={c.id} className="list-group-item list-group-item-action py-2" onClick={() => seleccionarCliente(c)}>
                        <strong>{c.nombres} {c.apellidos}</strong>
                        <span className="text-muted ms-2 small">CI: {c.identificacion}</span>
                      </button>
                    ))}
                  </div>
                )}
                {clienteBuscar.length === 0 && pacientesRecientes.length > 0 && (
                  <div>
                    <div className="small fw-semibold text-muted mb-2" style={{ letterSpacing: '0.3px' }}>
                      <i className="bi bi-clock-history me-1"></i>ÚLTIMOS PACIENTES REGISTRADOS
                    </div>
                    <div className="row g-2">
                      {pacientesRecientes.map(c => (
                        <div key={c.id} className="col-md-6 col-lg-4">
                          <button type="button" className="btn w-100 text-start p-2 d-flex align-items-center gap-2"
                            onClick={() => seleccionarCliente(c)}
                            style={{ background: 'white', border: '1px solid #E5E8EC', borderRadius: 6 }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1B4F72'; e.currentTarget.style.background = '#F8FAFC'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E8EC'; e.currentTarget.style.background = 'white'; }}
                          >
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: 32, height: 32, background: '#1B4F72', color: 'white', fontSize: '.7rem', fontWeight: 600 }}>
                              {(c.nombres?.[0] || '?').toUpperCase()}{(c.apellidos?.[0] || '').toUpperCase()}
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="small fw-medium text-truncate" style={{ color: '#2C3E50' }}>{c.nombres} {c.apellidos}</div>
                              <div className="text-muted text-truncate" style={{ fontSize: '.72rem' }}>{c.identificacion}{c.celular ? ` · ${c.celular}` : ''}</div>
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="text-center mt-3">
                      <small className="text-muted">Para otros pacientes, usa el buscador de arriba</small>
                    </div>
                  </div>
                )}
                {clienteBuscar.length === 0 && pacientesRecientes.length === 0 && (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-person-plus display-5 d-block mb-2 opacity-25"></i>
                    <p className="mb-2 small">Aún no hay pacientes registrados</p>
                    <button type="button" className="btn btn-sm" style={{ background: '#1B4F72', color: 'white' }} onClick={() => setShowCrearPaciente(true)}>
                      <i className="bi bi-plus-lg me-1"></i>Crear primer paciente
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="card border-primary">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <h5 className="mb-0"><i className="bi bi-person-circle me-2 text-primary"></i>{clienteSeleccionado.nombres} {clienteSeleccionado.apellidos}</h5>
                    {!isEditing && <button className="btn btn-sm btn-outline-danger" onClick={() => { setClienteSeleccionado(null); setForm(f => ({ ...f, cliente_id: '' })); }}>
                      <i className="bi bi-x-lg me-1"></i>Cambiar
                    </button>}
                  </div>
                  <div className="row g-2" style={{ fontSize: '0.9rem' }}>
                    <div className="col-md-3"><strong>CI:</strong> {clienteSeleccionado.identificacion}</div>
                    <div className="col-md-3"><strong>Edad:</strong> {calcEdad(clienteSeleccionado.fecha_nacimiento)}</div>
                    <div className="col-md-3"><strong>Celular:</strong> {clienteSeleccionado.celular || '-'}</div>
                    <div className="col-md-3"><strong>Ocupación:</strong> {clienteSeleccionado.ocupacion || '-'}</div>
                  </div>
                  <div className="row g-2 mt-2">
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold"># Factura</label>
                      <TInput value={form.numero_factura} onChange={e => setField('numero_factura', e.target.value)} placeholder="Número de factura" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Fecha de Registro</label>
                      <input type="datetime-local" className="form-control form-control-sm"
                        value={form.fecha_registro || ''}
                        onChange={e => setField('fecha_registro', e.target.value)} />
                      <small className="text-muted">Dejar vacío para fecha/hora actual</small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      // ══════════════════════════════════════
      // PASO 2: MOTIVO Y ANTECEDENTES
      // ══════════════════════════════════════
      case 'motivo':
        return (
          <div>
            <SectionTitle icon="bi-chat-left-text-fill" title="Motivo de Consulta y Antecedentes" />
            <div className="mb-3">
              <label className="form-label fw-semibold">Motivo de Consulta</label>
              <textarea className="form-control" rows="3" value={form.motivo_consulta} onChange={e => setField('motivo_consulta', e.target.value)} placeholder="Describa el motivo de la consulta..." />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Antecedentes Familiares</label>
              <textarea className="form-control" rows="2" value={form.antecedentes_familiares} onChange={e => setField('antecedentes_familiares', e.target.value)} placeholder="Diabetes, glaucoma, miopía..." />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Antecedentes Personales</label>
              <textarea className="form-control" rows="2" value={form.antecedentes_personales} onChange={e => setField('antecedentes_personales', e.target.value)} placeholder="Enfermedades, medicamentos, cirugías..." />
            </div>
          </div>
        );

      // ══════════════════════════════════════
      // PASO 3: AGUDEZA VISUAL
      // ══════════════════════════════════════
      case 'agudeza':
        return (
          <div>
            <SectionTitle icon="bi-eye-fill" title="Agudeza Visual" />
            <div className="row g-4">
              {/* AV Distancia */}
              <div className="col-md-5">
                <h6 className="text-center fw-bold mb-2" style={{ fontSize: '0.85rem' }}>AV DISTANCIA (VL)</h6>
                <table className="table table-sm table-bordered text-center mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light"><tr><th></th><th>OD</th><th>OI</th><th>AO</th></tr></thead>
                  <tbody>
                    <tr><td className="fw-bold bg-light">SC</td>
                      <td><AvSel value={form.av_distancia_od_sc} onChange={e => setField('av_distancia_od_sc', e.target.value)} /></td>
                      <td><AvSel value={form.av_distancia_oi_sc} onChange={e => setField('av_distancia_oi_sc', e.target.value)} /></td>
                      <td><AvSel value={form.av_distancia_ao_sc} onChange={e => setField('av_distancia_ao_sc', e.target.value)} /></td>
                    </tr>
                    <tr><td className="fw-bold bg-light">CC</td>
                      <td><AvSel value={form.av_distancia_od_cc} onChange={e => setField('av_distancia_od_cc', e.target.value)} /></td>
                      <td><AvSel value={form.av_distancia_oi_cc} onChange={e => setField('av_distancia_oi_cc', e.target.value)} /></td>
                      <td><AvSel value={form.av_distancia_ao_cc} onChange={e => setField('av_distancia_ao_cc', e.target.value)} /></td>
                    </tr>
                    <tr><td className="fw-bold bg-light">PH</td>
                      <td><AvSel value={form.av_distancia_od_ph} onChange={e => setField('av_distancia_od_ph', e.target.value)} /></td>
                      <td><AvSel value={form.av_distancia_oi_ph} onChange={e => setField('av_distancia_oi_ph', e.target.value)} /></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* AV Próxima */}
              <div className="col-md-5">
                <h6 className="text-center fw-bold mb-2" style={{ fontSize: '0.85rem' }}>AV PRÓXIMA (VP)</h6>
                <table className="table table-sm table-bordered text-center mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light"><tr><th></th><th>OD</th><th>OI</th><th>AO</th></tr></thead>
                  <tbody>
                    <tr><td className="fw-bold bg-light">SC</td>
                      <td><AvSel value={form.av_proxima_od_sc} onChange={e => setField('av_proxima_od_sc', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_oi_sc} onChange={e => setField('av_proxima_oi_sc', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_ao_sc} onChange={e => setField('av_proxima_ao_sc', e.target.value)} /></td>
                    </tr>
                    <tr><td className="fw-bold bg-light">CC</td>
                      <td><AvSel value={form.av_proxima_od_cc} onChange={e => setField('av_proxima_od_cc', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_oi_cc} onChange={e => setField('av_proxima_oi_cc', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_ao_cc} onChange={e => setField('av_proxima_ao_cc', e.target.value)} /></td>
                    </tr>
                    <tr><td className="fw-bold bg-light">PH</td>
                      <td><AvSel value={form.av_proxima_od_ph} onChange={e => setField('av_proxima_od_ph', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_oi_ph} onChange={e => setField('av_proxima_oi_ph', e.target.value)} /></td>
                      <td><AvSel value={form.av_proxima_ao_ph} onChange={e => setField('av_proxima_ao_ph', e.target.value)} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Dominante */}
              <div className="col-md-2">
                <h6 className="text-center fw-bold mb-2" style={{ fontSize: '0.85rem' }}>DOMINANTE</h6>
                <table className="table table-sm table-bordered text-center" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light"><tr><th>OD</th><th>OI</th></tr></thead>
                  <tbody><tr>
                    <td><TInput value={form.ojo_dominante_od} onChange={e => setField('ojo_dominante_od', e.target.value)} /></td>
                    <td><TInput value={form.ojo_dominante_oi} onChange={e => setField('ojo_dominante_oi', e.target.value)} /></td>
                  </tr></tbody>
                </table>
              </div>
            </div>
          </div>
        );

      // ══════════════════════════════════════
      // PASO 4: REFRACCIÓN (Lensometría, Queratometría, Autorefractor, Subjetivo)
      // ══════════════════════════════════════
      case 'refraccion': {
        // rxTable como función helper (NO como componente) para evitar pérdida de foco
        const rxTable = (title, fieldOd, fieldOi, cols) => (
          <div className="mb-4">
            <h6 className="fw-bold text-uppercase mb-2" style={{ fontSize: '0.85rem' }}>{title}</h6>
            <table className="table table-sm table-bordered text-center" style={{ fontSize: '0.8rem' }}>
              <thead className="table-light"><tr><th></th>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
              <tbody>
                <tr><td className="fw-bold bg-light" style={{ width: 40 }}>OD</td>
                  {cols.map(c => <td key={c.key}><TInput value={form[fieldOd]?.[c.key]} onChange={e => setJsonField(fieldOd, c.key, e.target.value)} /></td>)}
                </tr>
                <tr><td className="fw-bold bg-light">OI</td>
                  {cols.map(c => <td key={c.key}><TInput value={form[fieldOi]?.[c.key]} onChange={e => setJsonField(fieldOi, c.key, e.target.value)} /></td>)}
                </tr>
              </tbody>
            </table>
          </div>
        );

        return (
          <div>
            <SectionTitle icon="bi-eyeglasses" title="Refracción" />
            <div className="row g-4">
              <div className="col-md-6">
                {rxTable("Lensometría", "lensometria_od", "lensometria_oi",
                  [{key:'esf',label:'ESF'},{key:'cyl',label:'CYL'},{key:'eje',label:'EJE'},{key:'add',label:'ADD'},{key:'dp',label:'DP'},{key:'alt',label:'ALT'}])}
              </div>
              <div className="col-md-6">
                {rxTable("Queratometría", "queratometria_od", "queratometria_oi",
                  [{key:'k1',label:'K1'},{key:'k2',label:'K2'},{key:'eje',label:'EJE'}])}
              </div>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                {rxTable("Autorefractor", "autorefractor_od", "autorefractor_oi",
                  [{key:'esf',label:'ESF'},{key:'cyl',label:'CYL'},{key:'eje',label:'EJE'},{key:'av',label:'AV'}])}
              </div>
              <div className="col-md-6">
                {rxTable("Subjetivo", "subjetivo_od", "subjetivo_oi",
                  [{key:'esf',label:'ESF'},{key:'cyl',label:'CYL'},{key:'eje',label:'EJE'},{key:'av',label:'AV'}])}
              </div>
            </div>
          </div>
        );
      }

      // ══════════════════════════════════════
      // PASO 5: RX FINAL + LENTES DE CONTACTO
      // ══════════════════════════════════════
      case 'rx_final':
        return (
          <div>
            <SectionTitle icon="bi-prescription2" title="RX Final" />
            <table className="table table-sm table-bordered text-center mb-4" style={{ fontSize: '0.8rem' }}>
              <thead className="table-light">
                <tr><th></th><th>ESF</th><th>CYL</th><th>EJE</th><th>AVL</th><th>AVC</th><th>AO</th><th>DP</th><th>NP</th><th>ADD</th><th>ALT</th></tr>
              </thead>
              <tbody>
                <tr><td className="fw-bold bg-light">OD</td>
                  {['esf','cyl','eje','avl','avc','ao','dp','np','add','alt'].map(k =>
                    <td key={k}><TInput value={form.rx_final_od?.[k]} onChange={e => setJsonField('rx_final_od', k, e.target.value)} /></td>
                  )}
                </tr>
                <tr><td className="fw-bold bg-light">OI</td>
                  {['esf','cyl','eje','avl','avc','ao','dp','np','add','alt'].map(k =>
                    <td key={k}><TInput value={form.rx_final_oi?.[k]} onChange={e => setJsonField('rx_final_oi', k, e.target.value)} /></td>
                  )}
                </tr>
              </tbody>
            </table>

            <SectionTitle icon="bi-disc" title="Lentes de Contacto" color="info" />
            <table className="table table-sm table-bordered text-center mb-4" style={{ fontSize: '0.8rem' }}>
              <thead className="table-light">
                <tr><th></th><th>Poder</th><th>Curva Base</th><th>Diámetro</th><th>Adición</th><th>Diseño</th><th>Material</th></tr>
              </thead>
              <tbody>
                <tr><td className="fw-bold bg-light">OD</td>
                  {['poder','curva_base','diametro','adicion','diseno','material'].map(k =>
                    <td key={k}><TInput value={form.contacto_od?.[k]} onChange={e => setJsonField('contacto_od', k, e.target.value)} /></td>
                  )}
                </tr>
                <tr><td className="fw-bold bg-light">OI</td>
                  {['poder','curva_base','diametro','adicion','diseno','material'].map(k =>
                    <td key={k}><TInput value={form.contacto_oi?.[k]} onChange={e => setJsonField('contacto_oi', k, e.target.value)} /></td>
                  )}
                </tr>
              </tbody>
            </table>

            <div className="form-check mt-3">
              <input type="checkbox" className="form-check-input" checked={form.generar_prescripcion}
                onChange={e => setField('generar_prescripcion', e.target.checked)} id="genRx" />
              <label className="form-check-label fw-semibold" htmlFor="genRx">
                <i className="bi bi-prescription2 me-1"></i>Generar prescripción automática desde RX Final
              </label>
            </div>
          </div>
        );

      // ══════════════════════════════════════
      // PASO 6: MOTILIDAD OCULAR
      // ══════════════════════════════════════
      case 'motilidad':
        return (
          <div>
            <SectionTitle icon="bi-arrows-move" title="Motilidad Ocular" />
            <div className="row g-4">
              {/* Hirschberg */}
              <div className="col-md-4">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>HIRSCHBERG</h6>
                <table className="table table-sm table-bordered text-center" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light"><tr><th></th><th>0</th><th>15</th><th>30</th><th>45</th></tr></thead>
                  <tbody>
                    <tr><td className="fw-bold bg-light">OD</td>
                      {['0','15','30','45'].map(v => (
                        <td key={v}>
                          <input type="radio" className="form-check-input" name="hirschberg_od"
                            checked={form.hirschberg_od === v}
                            onChange={() => {}}
                            onClick={() => setField('hirschberg_od', form.hirschberg_od === v ? '' : v)} />
                        </td>
                      ))}
                    </tr>
                    <tr><td className="fw-bold bg-light">OI</td>
                      {['0','15','30','45'].map(v => (
                        <td key={v}>
                          <input type="radio" className="form-check-input" name="hirschberg_oi"
                            checked={form.hirschberg_oi === v}
                            onChange={() => {}}
                            onClick={() => setField('hirschberg_oi', form.hirschberg_oi === v ? '' : v)} />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Campimetría */}
              <div className="col-md-5">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>CAMPIMETRÍA POR CONFRONTACIÓN</h6>
                <table className="table table-sm table-bordered text-center" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light"><tr><th></th><th>T100</th><th>N60</th><th>S60</th><th>I70</th></tr></thead>
                  <tbody>
                    <tr><td className="fw-bold bg-light">OD</td>
                      {['t100','n60','s60','i70'].map(k => (
                        <td key={k}><TInput value={form.campimetria_od?.[k]} onChange={e => setJsonField('campimetria_od', k, e.target.value)} /></td>
                      ))}
                    </tr>
                    <tr><td className="fw-bold bg-light">OI</td>
                      {['t100','n60','s60','i70'].map(k => (
                        <td key={k}><TInput value={form.campimetria_oi?.[k]} onChange={e => setJsonField('campimetria_oi', k, e.target.value)} /></td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Mov Oculares */}
              <div className="col-md-3">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>MOV. OCULARES</h6>
                <div className="mb-2"><label className="form-label small mb-0">OD</label>
                  <select className="form-select form-select-sm" value={form.mov_oculares_od} onChange={e => setField('mov_oculares_od', e.target.value)}>
                    <option value="">-</option><option>SPEC: Normal</option><option>SPEC: Alterado</option><option>SPEC: Con sacadas correctivas</option><option>Limitado</option><option>Nistagmo</option>
                  </select>
                </div>
                <div><label className="form-label small mb-0">OI</label>
                  <select className="form-select form-select-sm" value={form.mov_oculares_oi} onChange={e => setField('mov_oculares_oi', e.target.value)}>
                    <option value="">-</option><option>SPEC: Normal</option><option>SPEC: Alterado</option><option>SPEC: Con sacadas correctivas</option><option>Limitado</option><option>Nistagmo</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Cover Test / Foria / Tropía */}
            <div className="row g-3 mt-3">
              <div className="col-md-2"><label className="form-label small fw-bold">Cover Test OD</label>
                <TInput value={form.cover_test_od} onChange={e => setField('cover_test_od', e.target.value)} /></div>
              <div className="col-md-2"><label className="form-label small fw-bold">Cover Test OI</label>
                <TInput value={form.cover_test_oi} onChange={e => setField('cover_test_oi', e.target.value)} /></div>
              <div className="col-md-2"><label className="form-label small fw-bold">Foria</label>
                <TInput value={form.foria} onChange={e => setField('foria', e.target.value)} /></div>
              <div className="col-md-3"><label className="form-label small fw-bold">Tropía</label>
                <TInput value={form.tropia} onChange={e => setField('tropia', e.target.value)} /></div>
              <div className="col-md-3"><label className="form-label small fw-bold">Mag. Desviación</label>
                <TInput value={form.magnitud_desviacion} onChange={e => setField('magnitud_desviacion', e.target.value)} /></div>
            </div>
          </div>
        );

      // ══════════════════════════════════════
      // PASO 7: VISIÓN BINOCULAR
      // ══════════════════════════════════════
      case 'binocular':
        return (
          <div>
            <SectionTitle icon="bi-binoculars-fill" title="Visión Binocular y Tests Complementarios" />
            {/* PPC, OR, etc */}
            <table className="table table-sm table-bordered text-center mb-4" style={{ fontSize: '0.8rem' }}>
              <thead className="table-light">
                <tr><th></th><th>PPC</th><th>OR</th><th>LUZ +FR</th><th>Técnica</th><th>Ciclos</th><th>Kappa</th><th>Distancia</th><th>ARP</th><th>ARN</th><th>RFP</th><th>RFN</th></tr>
              </thead>
              <tbody>
                <tr><td className="fw-bold bg-light">OD</td>
                  <td><TInput value={form.ppc_od} onChange={e => setField('ppc_od', e.target.value)} /></td>
                  <td><TInput value={form.or_od} onChange={e => setField('or_od', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.luz_fr} onChange={e => setField('luz_fr', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.tecnica} onChange={e => setField('tecnica', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.ciclos} onChange={e => setField('ciclos', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.kappa} onChange={e => setField('kappa', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.distancia} onChange={e => setField('distancia', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.arp} onChange={e => setField('arp', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.arn} onChange={e => setField('arn', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.rfp} onChange={e => setField('rfp', e.target.value)} /></td>
                  <td rowSpan="2"><TInput value={form.rfn} onChange={e => setField('rfn', e.target.value)} /></td>
                </tr>
                <tr><td className="fw-bold bg-light">OI</td>
                  <td><TInput value={form.ppc_oi} onChange={e => setField('ppc_oi', e.target.value)} /></td>
                  <td><TInput value={form.or_oi} onChange={e => setField('or_oi', e.target.value)} /></td>
                </tr>
              </tbody>
            </table>

            {/* Tests complementarios */}
            <div className="row g-4">
              <div className="col-md-4">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>LUCES DE WORTH</h6>
                <div className="mb-2"><label className="form-label small mb-0">Lejos</label>
                  <TInput value={form.luces_worth_lejos} onChange={e => setField('luces_worth_lejos', e.target.value)} /></div>
                <div><label className="form-label small mb-0">Cerca</label>
                  <TInput value={form.luces_worth_cerca} onChange={e => setField('luces_worth_cerca', e.target.value)} /></div>
              </div>
              <div className="col-md-4">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>TEST ISHIHARA</h6>
                <div className="mb-2"><label className="form-label small mb-0">OD</label>
                  <TInput value={form.test_ishihara_od} onChange={e => setField('test_ishihara_od', e.target.value)} /></div>
                <div><label className="form-label small mb-0">OI</label>
                  <TInput value={form.test_ishihara_oi} onChange={e => setField('test_ishihara_oi', e.target.value)} /></div>
              </div>
              <div className="col-md-4">
                <h6 className="fw-bold mb-2" style={{ fontSize: '0.85rem' }}>PRESIÓN INTRAOCULAR</h6>
                <div className="mb-2"><label className="form-label small mb-0">OD</label>
                  <TInput value={form.presion_intraocular_od} onChange={e => setField('presion_intraocular_od', e.target.value)} placeholder="mmHg" /></div>
                <div><label className="form-label small mb-0">OI</label>
                  <TInput value={form.presion_intraocular_oi} onChange={e => setField('presion_intraocular_oi', e.target.value)} placeholder="mmHg" /></div>
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label fw-semibold">Test Adicionales</label>
              <textarea className="form-control form-control-sm" rows="2" value={form.test_adicionales || ''}
                onChange={e => setField('test_adicionales', e.target.value)} />
            </div>
          </div>
        );

      // ══════════════════════════════════════
      // PASO 8: BIOMICROSCOPÍA
      // ══════════════════════════════════════
      case 'biomicro': {
        const bioFields = [
          { key: 'cornea', label: 'Córnea' },
          { key: 'cristalino', label: 'Cristalino' },
          { key: 'pupila', label: 'Pupila' },
          { key: 'iris', label: 'Iris' },
          { key: 'pestanas', label: 'Pestañas' },
          { key: 'conjuntiva_bulbar', label: 'Conjuntiva Bulbar' },
          { key: 'conjuntiva_tarsal', label: 'Conjuntiva Tarsal' },
          { key: 'orbita', label: 'Órbita' },
          { key: 'pliegue_semilunar', label: 'Pliegue Semilunar' },
          { key: 'caruncula', label: 'Carúncula' },
          { key: 'conductos_lagrimales', label: 'Conductos Lagrimales' },
          { key: 'parpado_superior', label: 'Párpado Superior' },
          { key: 'camara_anterior', label: 'Cámara Anterior' },
          { key: 'parpado_inferior', label: 'Párpado Inferior' }
        ];
        return (
          <div>
            <SectionTitle icon="bi-microscope" title="Biomicroscopía" />
            <div className="row g-3 mb-4">
              {/* Ojo OD */}
              <div className="col-md-3 text-center">
                <EyeSvg side="OD" />
              </div>
              {/* Tabla central con descripciones */}
              <div className="col-md-6">
                <table className="table table-sm table-bordered" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light text-center">
                    <tr><th style={{ width: '25%' }}>OD</th><th>Estructura</th><th style={{ width: '25%' }}>OI</th></tr>
                  </thead>
                  <tbody>
                    {bioFields.map(f => (
                      <tr key={f.key}>
                        <td><TInput value={form.biomicroscopia_od?.[f.key]} onChange={e => setJsonField('biomicroscopia_od', f.key, e.target.value)} /></td>
                        <td className="fw-semibold text-center bg-light">{f.label}</td>
                        <td><TInput value={form.biomicroscopia_oi?.[f.key]} onChange={e => setJsonField('biomicroscopia_oi', f.key, e.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Ojo OI */}
              <div className="col-md-3 text-center">
                <EyeSvg side="OI" />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Otros</label>
              <textarea className="form-control form-control-sm" rows="2" value={form.biomicroscopia_otros}
                onChange={e => setField('biomicroscopia_otros', e.target.value)} />
            </div>

            {/* Reflejos Pupilares */}
            <SectionTitle icon="bi-circle" title="Reflejos Pupilares" color="warning" />
            <table className="table table-sm table-bordered text-center" style={{ fontSize: '0.8rem', maxWidth: 500 }}>
              <thead className="table-light">
                <tr><th></th><th>Acomodativo</th><th>Fotomotor</th><th>Consensual</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold bg-light">AO</td>
                  <td>
                    <TInput value={form.reflejo_acomodativo?.ao_miosis} onChange={e => setJsonField('reflejo_acomodativo', 'ao_miosis', e.target.value)} placeholder="Miosis" />
                  </td>
                  <td>
                    <TInput value={form.reflejo_fotomotor?.miosis} onChange={e => setJsonField('reflejo_fotomotor', 'miosis', e.target.value)} placeholder="Miosis" />
                  </td>
                  <td>
                    <TInput value={form.reflejo_consensual_od} onChange={e => setField('reflejo_consensual_od', e.target.value)} placeholder="OD" />
                  </td>
                </tr>
                <tr>
                  <td className="fw-bold bg-light">AO</td>
                  <td>
                    <TInput value={form.reflejo_acomodativo?.ao_convergencia} onChange={e => setJsonField('reflejo_acomodativo', 'ao_convergencia', e.target.value)} placeholder="Convergencia" />
                  </td>
                  <td>
                    <TInput value={form.reflejo_fotomotor?.midriasis} onChange={e => setJsonField('reflejo_fotomotor', 'midriasis', e.target.value)} placeholder="Midriasis" />
                  </td>
                  <td>
                    <TInput value={form.reflejo_consensual_oi} onChange={e => setField('reflejo_consensual_oi', e.target.value)} placeholder="OI" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }

      // ══════════════════════════════════════
      // PASO 9: FONDO DE OJO
      // ══════════════════════════════════════
      case 'fondo': {
        const fondoFields = [
          { key: 'av_temp_sup', label: 'A/V Temp Sup' },
          { key: 'av_temp_inf', label: 'A/V Temp Inf' },
          { key: 'av_nasal_sup', label: 'A/V Nasal Sup' },
          { key: 'av_nasal_inf', label: 'A/V Nasal Inf' },
          { key: 'retina', label: 'Retina' },
          { key: 'macula', label: 'Mácula' },
          { key: 'excavacion', label: 'Excavación' },
          { key: 'vasos', label: 'Vasos' },
          { key: 'papila', label: 'Papila' },
          { key: 'fijacion', label: 'Fijación' },
          { key: 'color', label: 'Color' },
          { key: 'borde', label: 'Borde' }
        ];
        return (
          <div>
            <SectionTitle icon="bi-circle" title="Fondo de Ojo" />
            <div className="row g-3 mb-4">
              {/* Fondo OD */}
              <div className="col-md-3 text-center">
                <FundusSvg side="OD" />
              </div>
              {/* Tabla central */}
              <div className="col-md-6">
                <table className="table table-sm table-bordered" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light text-center">
                    <tr><th style={{ width: '25%' }}>OD</th><th>Descripción</th><th style={{ width: '25%' }}>OI</th></tr>
                  </thead>
                  <tbody>
                    {fondoFields.map(f => (
                      <tr key={f.key}>
                        <td><TInput value={form.fondo_ojo_od?.[f.key]} onChange={e => setJsonField('fondo_ojo_od', f.key, e.target.value)} /></td>
                        <td className="fw-semibold text-center bg-light">{f.label}</td>
                        <td><TInput value={form.fondo_ojo_oi?.[f.key]} onChange={e => setJsonField('fondo_ojo_oi', f.key, e.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Fondo OI */}
              <div className="col-md-3 text-center">
                <FundusSvg side="OI" />
              </div>
            </div>
          </div>
        );
      }

      // ══════════════════════════════════════
      // PASO 10: DIAGNÓSTICO Y CONCLUSIÓN
      // ══════════════════════════════════════
      case 'diagnostico':
        return (
          <div>
            <SectionTitle icon="bi-clipboard2-pulse-fill" title="Diagnóstico y Tratamiento" />
            <div className="mb-3">
              <label className="form-label fw-semibold">Diagnóstico *</label>
              <textarea className="form-control" rows="3" value={form.diagnostico} onChange={e => setField('diagnostico', e.target.value)} required />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Tratamiento</label>
              <textarea className="form-control" rows="2" value={form.tratamiento} onChange={e => setField('tratamiento', e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Observación</label>
              <textarea className="form-control" rows="2" value={form.observacion || ''} onChange={e => setField('observacion', e.target.value)} />
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Disposición</label>
                <textarea className="form-control form-control-sm" rows="2" value={form.disposicion || ''} onChange={e => setField('disposicion', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Conducta</label>
                <textarea className="form-control form-control-sm" rows="2" value={form.conducta || ''} onChange={e => setField('conducta', e.target.value)} />
              </div>
            </div>

            {/* Signos Vitales */}
            <SectionTitle icon="bi-heart-pulse" title="Signos Vitales" color="danger" />
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label small fw-semibold">P.A. Sistólica</label>
                <TInput value={form.presion_sistolica} onChange={e => setField('presion_sistolica', e.target.value)} placeholder="mmHg" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">P.A. Diastólica</label>
                <TInput value={form.presion_diastolica} onChange={e => setField('presion_diastolica', e.target.value)} placeholder="mmHg" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Saturación O2</label>
                <TInput value={form.saturacion_o2} onChange={e => setField('saturacion_o2', e.target.value)} placeholder="%" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Glucosa</label>
                <TInput value={form.glucosa} onChange={e => setField('glucosa', e.target.value)} placeholder="mg/dL" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Triglicéridos</label>
                <TInput value={form.trigliceridos} onChange={e => setField('trigliceridos', e.target.value)} placeholder="mg/dL" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">TTP</label>
                <TInput value={form.ttp} onChange={e => setField('ttp', e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">ATP</label>
                <TInput value={form.atp} onChange={e => setField('atp', e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Colesterol</label>
                <TInput value={form.colesterol} onChange={e => setField('colesterol', e.target.value)} placeholder="mg/dL" />
              </div>
            </div>

            {/* Estado */}
            <div className="row g-3 mt-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Estado</label>
                <select className="form-select form-select-sm" value={form.estado} onChange={e => setField('estado', e.target.value)}>
                  <option value="completada">Completada</option>
                  <option value="borrador">Borrador</option>
                  <option value="anulada">Anulada</option>
                </select>
              </div>
              <div className="col-md-8">
                <label className="form-label fw-semibold">Notas Adicionales</label>
                <textarea className="form-control form-control-sm" rows="1" value={form.notas_adicionales} onChange={e => setField('notas_adicionales', e.target.value)} />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary"></div><p className="mt-2">Cargando historia clínica...</p></div>;
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="bi bi-clipboard2-pulse me-2 text-primary"></i>
          {isEditing ? 'Editar Historia Clínica' : 'Nueva Historia Clínica'}
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-1"></i>Volver
        </button>
      </div>

      {/* Stepper */}
      <div className="card mb-3">
        <div className="card-body py-2 px-2">
          <div className="d-flex flex-wrap gap-1 justify-content-center">
            {STEPS.map((s, i) => (
              <button key={s.key}
                className={`btn btn-sm ${i === step ? 'btn-primary' : i < step ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                onClick={() => { if (i === 0 || clienteSeleccionado) setStep(i); }}
                style={{ fontSize: '0.72rem', padding: '3px 8px' }}
              >
                <i className={`bi ${s.icon} me-1`}></i>
                <span className="d-none d-md-inline">{s.label}</span>
                <span className="d-md-none">{i + 1}</span>
              </button>
            ))}
          </div>
          {/* Progress */}
          <div className="progress mt-2" style={{ height: 3 }}>
            <div className="progress-bar bg-primary" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="card">
        <div className="card-body">
          {renderStep()}
        </div>
      </div>

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-3">
        <button className="btn btn-outline-secondary" onClick={prevStep} disabled={step === 0}>
          <i className="bi bi-chevron-left me-1"></i>Anterior
        </button>
        <div className="d-flex gap-2">
          {/* Botón Cobrar atención: solo si la HC ya está guardada y no tiene atención vinculada */}
          {isEditing && !form.atencion_servicio_id && !atencionCobrada && form.estado === 'completada' && (
            <button
              className="btn btn-lg fw-bold"
              style={{ background: 'linear-gradient(135deg, #0984e3 0%, #6c5ce7 100%)', color: 'white', border: 'none' }}
              onClick={() => setShowCobrarModal(true)}
              title="Emitir comprobante de atención"
            >
              <i className="bi bi-cash-stack me-2"></i>Cobrar atención
            </button>
          )}
          {isEditing && (form.atencion_servicio_id || atencionCobrada) && (
            <button
              className="btn btn-success btn-lg"
              disabled
              title="Esta atención ya fue cobrada"
            >
              <i className="bi bi-check-circle-fill me-2"></i>Atención cobrada
            </button>
          )}
          {step === STEPS.length - 1 ? (
            <button className="btn btn-success btn-lg" onClick={handleSubmit} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> :
                <><i className="bi bi-check-lg me-2"></i>{isEditing ? 'Actualizar' : 'Guardar Historia Clínica'}</>}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={nextStep}>
              Siguiente<i className="bi bi-chevron-right ms-1"></i>
            </button>
          )}
        </div>
      </div>

      {/* Modal Cobrar Atención */}
      <ModalCobrarAtencion
        show={showCobrarModal}
        onClose={() => setShowCobrarModal(false)}
        clientePreseleccionado={clienteSeleccionado}
        historiaClinicaId={id ? parseInt(id) : null}
        optometristaId={form.optometrista_id || null}
        onSuccess={(data) => {
          setAtencionCobrada(data);
          toast.success(`Comprobante ${data.comprobante.numero} emitido`);
        }}
      />

      {/* Modal Crear Paciente Rápido */}
      {showCrearPaciente && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(21, 67, 96, 0.55)' }} onClick={(e) => e.target === e.currentTarget && setShowCrearPaciente(false)}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0">
              <div className="modal-header" style={{ background: '#1B4F72', color: 'white' }}>
                <h6 className="modal-title fw-semibold mb-0">
                  <i className="bi bi-person-plus me-2"></i>Nuevo paciente
                </h6>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCrearPaciente(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-5">
                    <label className="small text-muted mb-1">Tipo ID</label>
                    <select className="form-select form-select-sm" value={nuevoPac.tipo_identificacion} onChange={e => setNuevoPac({ ...nuevoPac, tipo_identificacion: e.target.value })}>
                      <option value="cedula">Cédula</option>
                      <option value="ruc">RUC</option>
                      <option value="pasaporte">Pasaporte</option>
                    </select>
                  </div>
                  <div className="col-7">
                    <label className="small text-muted mb-1">Identificación *</label>
                    <input className="form-control form-control-sm" value={nuevoPac.identificacion} onChange={e => setNuevoPac({ ...nuevoPac, identificacion: e.target.value })} autoFocus />
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Nombres *</label>
                    <input className="form-control form-control-sm" value={nuevoPac.nombres} onChange={e => setNuevoPac({ ...nuevoPac, nombres: e.target.value })} />
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Apellidos *</label>
                    <input className="form-control form-control-sm" value={nuevoPac.apellidos} onChange={e => setNuevoPac({ ...nuevoPac, apellidos: e.target.value })} />
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Fecha nacimiento</label>
                    <input type="date" className="form-control form-control-sm" value={nuevoPac.fecha_nacimiento} onChange={e => setNuevoPac({ ...nuevoPac, fecha_nacimiento: e.target.value })} />
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Género</label>
                    <select className="form-select form-select-sm" value={nuevoPac.genero} onChange={e => setNuevoPac({ ...nuevoPac, genero: e.target.value })}>
                      <option value="">—</option>
                      <option value="masculino">Masculino</option>
                      <option value="femenino">Femenino</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Celular</label>
                    <input className="form-control form-control-sm" value={nuevoPac.celular} onChange={e => setNuevoPac({ ...nuevoPac, celular: e.target.value })} />
                  </div>
                  <div className="col-6">
                    <label className="small text-muted mb-1">Email</label>
                    <input type="email" className="form-control form-control-sm" value={nuevoPac.email} onChange={e => setNuevoPac({ ...nuevoPac, email: e.target.value })} />
                  </div>
                  <div className="col-12">
                    <label className="small text-muted mb-1">Ocupación</label>
                    <input className="form-control form-control-sm" value={nuevoPac.ocupacion} onChange={e => setNuevoPac({ ...nuevoPac, ocupacion: e.target.value })} />
                  </div>
                </div>
                <div className="mt-2">
                  <small className="text-muted">* Campos obligatorios. Luego puedes completar más datos desde Pacientes.</small>
                </div>
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-sm btn-light" onClick={() => setShowCrearPaciente(false)} disabled={guardandoPac}>Cancelar</button>
                <button type="button" className="btn btn-sm" style={{ background: '#1B4F72', color: 'white' }} onClick={crearPacienteRapido} disabled={guardandoPac}>
                  {guardandoPac ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> : <><i className="bi bi-check-lg me-1"></i>Crear y seleccionar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoriaClinica;
